import React, { useEffect, useRef, useState, useCallback } from 'react';

import { TextInput, Platform } from 'react-native';
import { usePrevious } from '../../../common/utils/hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { applyBrandFont } from '../../design/typography';
import { useIsOnCard } from '../../design/fieldSurface';
import { useDidUpdateEffect } from '../../../common/utils/hooks/useDidUpdateEffect';

const InputNumber = ({
	value,
	multiplyByHundred,
	roundToSignificantFigures,
	autoFocus,
	decimalPlaces = 0,
	onChange,
	onChangeInternal,
	onConfirm,
	field,
	nullable,
	onBlur,
	onFocus,
	disabled = false,
	placeholder,
	style,
	textAlign,
}) => {
	const [internalValue, setInternalValue] = useState('');
	const [displayValue, setDisplayValue] = useState('');
	const ref = useRef();
	const styles = useThemedStyles(createStyles);
	const isOnCard = useIsOnCard();
	const { theme } = useTheme();

	const _limitDecimalPlaces = useCallback((value) => {
		// Don't use !decimalPlaces - misses when value is 0
		// No strictly necessary with defaultProp of 0.
		if (decimalPlaces === undefined) {
			return value;
		}

		// If the input is currently empty, don't try to round
		// It will just result in NaN which requires later handling.
		// Furthermore, convert nulls to empty string.
		// Example: you have deleted the value from the field, and
		// in componentDidUpdate, the value get passed back into
		// another InputNumber, rendering the same value.
		if (value === '' || value === null) {
			return '';
		}

		// Round according to passed in decimal places prop
		const roundedValue = parseFloat(value).toFixed(decimalPlaces);

		return roundedValue;
	}, [decimalPlaces]);

	useEffect(() => {
		// If value is supplied, this component is edit inplace
		// initialise the intermediate and display values as the value.
		// NOTE: can't use (!value, in case value is zero)
		if ((value !== undefined) && (value !== null)) {

			let initialDisplayValue = value;

			// Convert the display value from value when initialising
			if (multiplyByHundred) {
				initialDisplayValue = _decreaseByTwoDecimals(initialDisplayValue);
			}

			// In all cases, limit decimal places
			initialDisplayValue = _limitDecimalPlaces(initialDisplayValue);

			// round to significant figures
			initialDisplayValue = roundToSignificantFigures ? Number(initialDisplayValue).toString() : initialDisplayValue;

			setInternalValue(value);
			setDisplayValue(initialDisplayValue);
		}
		else {
			setInternalValue(null);
			setDisplayValue('');
		}
		// set for android autoFocus due to keyboard show on default
		if (autoFocus) {
			if (Platform.OS === 'ios') {
				ref.current?.focus();
			}
			else {
				setTimeout(() => ref.current?.focus(), 50);
			}
		}
	}, [_limitDecimalPlaces, autoFocus, multiplyByHundred, roundToSignificantFigures, value, setInternalValue, setDisplayValue]);

	const prevValue = usePrevious(value);

	useDidUpdateEffect(() => {
		// IMPORTANT! Only relevantly update when receiving new props!
		// We want to 'reset state' upon some new value coming in from the back end.
		if (value === prevValue) {
			return;
		}
		// The relevant comparison value is the current internalValue
		if (internalValue !== value) {

			if (value === null || value === undefined) {
				setInternalValue(null);
				setDisplayValue('');
				return;
			}

			let newDisplayValue = _limitDecimalPlaces(value);

			if (multiplyByHundred && (newDisplayValue !== '')) {
				newDisplayValue = _decreaseByTwoDecimals(newDisplayValue);
			}

			// round to significant figures
			newDisplayValue = roundToSignificantFigures ? Number(newDisplayValue).toString() : newDisplayValue;

			// eslint-disable-next-line react/no-did-update-set-state
			setInternalValue(value);
			setDisplayValue(newDisplayValue);
		}
	}, [value]);

	const _increaseByTwoDecimals = (value) => {
		return Math.round(value * 100);
	};

	const _decreaseByTwoDecimals = (value) => {
		return (value / 100).toFixed(2);
	};

	const ignoreAutoFocusChangeRef = useRef(false);
	const prevAutoFocus = usePrevious(autoFocus);

	useEffect(() => {
		const hasInitialValue = value !== undefined && value !== null && value !== '';
		if (autoFocus && hasInitialValue && autoFocus !== prevAutoFocus) {
			ignoreAutoFocusChangeRef.current = true;
			return;
		}
		if (!autoFocus || !hasInitialValue) {
			ignoreAutoFocusChangeRef.current = false;
		}
	}, [autoFocus, prevAutoFocus, value]);

	const _onChange = value => {
		if (ignoreAutoFocusChangeRef.current && value === '') {
			ignoreAutoFocusChangeRef.current = false;
			return;
		}
		ignoreAutoFocusChangeRef.current = false;

		// to allow comma - replace it with dot
		value = value.replace(',', '.');

		// Allows only numbers and a decimal
		let re = /^[0-9\b.]+$/;

		// Don't allow decimals if decimal places is 0
		if (decimalPlaces === 0) {
			re = /^[0-9\b]+$/;
		}

		let MAX_SAFE_INTEGER = 2147483647;
		if (multiplyByHundred) {
			MAX_SAFE_INTEGER = 21474835;
		}

		// Don't allow numbers greater than the maximum
		// positive value for a 32-bit signed binary integer
		if (parseFloat(value, 10) > MAX_SAFE_INTEGER) {
			return;
		}

		let newInternalValue = value;
		if (multiplyByHundred && (newInternalValue !== '')) {
			newInternalValue = _increaseByTwoDecimals(newInternalValue);
		}

		// if value is not blank, then test the regex
		if (value === '' || re.test(value)) {
			setInternalValue(newInternalValue);
			setDisplayValue(value);

			if (onChangeInternal) {
				onChangeInternal(newInternalValue !== '' ? Number(newInternalValue) : newInternalValue);
			}
		}
	};

	const _onBlur = useCallback(() => {

		let newDisplayValue = internalValue;

		// Multiply by one hundred (but not if empty string)
		if (multiplyByHundred && (newDisplayValue !== '') && (newDisplayValue !== null)) {
			newDisplayValue = _decreaseByTwoDecimals(newDisplayValue);
		}

		// Round the value upon de-focusing
		newDisplayValue = _limitDecimalPlaces(newDisplayValue);

		let newInternalValue = parseFloat(_limitDecimalPlaces(internalValue), 10);

		// Set an empty string for uninstantiated values etc,
		// so value is always supplied to the controlled input.
		if ((newDisplayValue === undefined) || (newDisplayValue === null) || isNaN(newDisplayValue)) {
			newDisplayValue = '';
		}

		// Avoid sending empty strings
		if (newDisplayValue === '') {

			if (nullable === false) {
				newInternalValue = undefined;
			}
			else {
				newInternalValue = null;
			}
		}
		else {
			// round to significant figures
			newDisplayValue = roundToSignificantFigures ? Number(newDisplayValue).toString() : newDisplayValue;
		}

		setDisplayValue(newDisplayValue);
		setInternalValue(newInternalValue);

		if (newInternalValue !== value) {
			if (onConfirm) {
				onConfirm({ [field]: newInternalValue });
			}
			if (onChange) {
				onChange(newInternalValue);
			}
		}

		if (onBlur) {
			onBlur(newInternalValue);
		}
	}, [_limitDecimalPlaces, field, internalValue, multiplyByHundred, setDisplayValue, setInternalValue, onChange, onConfirm, onBlur, nullable, value, roundToSignificantFigures]);

	return (
		<TextInput
			value={displayValue}
			onChangeText={_onChange}
			selectTextOnFocus={true}
			// Native needs multiline for selectTextOnFocus; on web it renders a textarea
			// whose text sits at the top of the box, while a single-line input centres it.
			multiline={Platform.OS !== 'web'}
			// Pass through props
			style={applyBrandFont([
				styles.input,
				isOnCard && styles.inputOnCard,
				{
					paddingHorizontal: 10,
					height: 44,
					textAlign: textAlign || 'right',
					paddingTop: Platform.OS === 'ios' ? 12 : 10,
					paddingBottom: Platform.OS === 'ios' ? 12 : 10,
					textAlignVertical: 'center',
				},
				style,
				disabled && styles.disabledInput,
			], theme.ui.areBrandFontsLoaded)}
			editable={!disabled}
			placeholder={placeholder}
			placeholderTextColor={theme.ui.colors.textSecondary}
			onBlur={_onBlur}
			onFocus={onFocus}
			ref={ref}
			keyboardType="numeric"
		/>
	);
};
const createStyles = ({ ui: { colors, radius, type } }) => ({
	input: {
		// No lineHeight: it shifts single-line input text on iOS.
		fontSize: type.rowText.fontSize,
		fontFamily: type.rowText.fontFamily,
		borderRadius: radius.control,
		backgroundColor: colors.card,
		color: colors.text,

		borderWidth: 1,
		borderStyle: 'solid',
		borderColor: colors.borderStrong,

		width: 100,
	},
	// Inside a Card the card-coloured fill would vanish (design/fieldSurface.js).
	inputOnCard: {
		backgroundColor: colors.canvas,
	},
	// Keep the outline: disabledBg matches the card in Light.
	disabledInput: {
		color: colors.disabledText,
		backgroundColor: colors.disabledBg,
		borderColor: colors.borderStrong,
	},
});

export default InputNumber;
